import styled from 'styled-components';
import { colors, Typography } from 'syngenta-digital-cropwise-react-ui-kit';
import IconChevronRight from 'components/Icons/IconChevronRight';
import IconSort from 'components/Icons/IconSort';
import breakpoints from 'base/constants/breakpoints';

interface IIconProps {
  disabled?: boolean;
  isOpened?: boolean;
}

const IconChevronRightStyled = styled(IconChevronRight)<IIconProps>`
  color: ${colors.neutral60};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transform: ${({ isOpened }) => (isOpened ? 'rotate(-90deg)' : 'rotate(90deg)')};
`;

const DivFilterStyled = styled(Typography.Text)`
  cursor: pointer;
`;

const IconSortStyled = styled(IconSort)<IIconProps>`
  cursor: pointer;
`;

const ColumnTitle = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: -0.0075em;
  color: ${colors.neutral100};
  margin: 0;
  display: inline-block;
  margin-right: 9.5px;
`;

const TableStyled = styled.table`
  border: 1px solid ${colors.neutral30};
  margin-top: 56px;
  width: 100%;
  table-layout: fixed;

  td {
    padding-right: 5px;
  }

  @media screen and (min-width: 768px) and (max-width: 1024px) {
    width: 100%;

    td {
      width: auto;
      white-space: normal;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Ensure the first column (field names) has adequate width */
    td:first-child {
      width: 180px;
      min-width: 180px;
      max-width: 180px;
    }

    /* Adjust other columns to distribute remaining space */
    td:not(:first-child):not(:last-child) {
      width: calc((100% - 180px) / 6);
    }

    /* Last column for the expand/collapse icon */
    td:last-child {
      width: 30px;
      min-width: 30px;
    }
  }
`;

const FieldCeilContainer = styled.div`
  display: flex;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 12px;

  /* iPad/tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    padding-left: 6px;
    padding-right: 2px;
    width: 100%;
    min-width: 150px;
  }
`;

const FieldCeilPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 28px;

  /* iPad/tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    padding-right: 10px;
    flex: 0 0 auto;

    /* Make the field preview smaller on iPad */
    svg {
      width: 40px;
      height: 40px;
    }
  }
`;

const FieldCeilTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  /* iPad/tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    width: 100%;
    justify-content: flex-start;

    div {
      width: 100%;
    }
  }
`;

const TrStyled = styled.tr`
  height: 52px;
  background-color: ${colors.neutral10};
  border-top: 1px solid ${colors.neutral50};
`;

interface ITdMainRowProps {
  hasPaddingRight?: boolean;
  centerText?: boolean;
  hasMarginLeft?: boolean;
  skuColumn?: boolean;
}

const TrTitleRow = styled.tr`
  height: 52px;
  background-color: ${colors.neutral20};
  padding-top: 16px;
  padding-bottom: 16px;
`;

const TdMainRow = styled.td<ITdMainRowProps>`
  padding-right: ${({ hasPaddingRight }) => (hasPaddingRight ? '8px' : undefined)};
  text-align: ${({ centerText }) => (centerText ? 'center' : undefined)};

  /* iPad and tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    padding: 8px 4px;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TdColumnTitle = styled.td<ITdMainRowProps>`
  text-align: ${({ centerText }) => (centerText ? 'center' : undefined)};
  padding-left: ${({ hasMarginLeft }) => (hasMarginLeft ? '16px' : undefined)};

  /* iPad and tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    padding: 8px 4px;
    ${({ skuColumn }) =>
    skuColumn &&
      `
      width: 120px;
      text-align: left;
    `}
  }

  @media screen and (min-width: 820px) and (max-width: 1180px) {
    ${({ skuColumn }) =>
    skuColumn &&
      `
      width: 150px;
      text-align: left;
    `}
  }

  @media screen and (max-width: 819px), (min-width: 1181px) {
    ${({ skuColumn }) =>
    skuColumn &&
      `
      width: 400px;
      text-align: left;
    `}
  }
`;

interface ITrExpandableRowProps {
  displayBorder?: boolean;
}

const TrExpandableRow = styled.tr<ITrExpandableRowProps>`
  height: 52px;
  border-bottom: ${({ displayBorder }) =>
    (displayBorder ? `1px solid ${colors.neutral10}` : undefined)};
`;

const FieldNameText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.0075em;
  color ${colors.neutral100};
  margin: 0;
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  
  /* iPad/tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.005em;
    white-space: normal;
    overflow: visible;
  }
  
  @media screen and ${breakpoints.maxWidth.sm} {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.12px;
    color: #14151C;
  }
`;

const FieldAreaText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: -0.0075em;
  color ${colors.neutral60};
  margin: 0;
  
  /* iPad/tablet specific styles */
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
  }
  
  @media screen and ${breakpoints.maxWidth.sm} {
    letter-spacing: -0.105px;
    color: #696F88;
  }
`;

const RankText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color ${colors.neutral100};
  margin: 0;
`;

const VarietyText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color ${colors.neutral100};
  margin: 0;
`;
const SkuText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color ${colors.neutral100};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CommonText = styled(Typography.Text)`
  font-family: 'Noto Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color ${colors.neutral100};
  margin: 0;
  
  @media screen and (min-width: 768px) and (max-width: 1024px) {
    display: inline-block;
    width: 100%;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export {
  IconChevronRightStyled,
  ColumnTitle,
  TableStyled,
  FieldCeilContainer,
  FieldCeilPreviewContainer,
  TrStyled,
  FieldNameText,
  FieldAreaText,
  RankText,
  VarietyText,
  CommonText,
  TrExpandableRow,
  FieldCeilTextContainer,
  TdMainRow,
  DivFilterStyled,
  IconSortStyled,
  TdColumnTitle,
  TrTitleRow,
  SkuText,
};
